/**
 * Timeline Query Functions
 * 
 * Fetches student day timelines and attendance summaries.
 * @module fehlzeiten/services/queries/timelineQueries
 */

import { supabase } from '../../../../../lib/supabase';
import type { StudentDayTimelineParams, TimelineEntry } from '../../types/services';

/**
 * Fetches a student's day timeline showing all lessons and attendance status.
 */
export async function fetchStudentDayTimeline(
    params: StudentDayTimelineParams
): Promise<TimelineEntry[]> {
    const { data, error } = await supabase.rpc('get_student_day_timeline' as never, {
        p_student_id: params.studentId,
        p_date: params.date,
    } as never);

    if (error) {
        throw new Error(`Failed to fetch timeline: ${error.message}`);
    }

    return (Array.isArray(data) ? data : []).map((row: Record<string, unknown>) => ({
        lessonId: row.lesson_id as string,
        periodLabel: '',
        startTime: (row.period_start_time as string) || '',
        endTime: (row.period_end_time as string) || '',
        subjectName: (row.subject_name_full as string) || (row.course_name as string) || null,
        status: row.status as string | null,
        hasAttendance: !!row.status,
        diaryEntryText: (row.diary_entry_text as string) || null,
        isCancelled: (row.is_cancelled as boolean) || false,
        substituteIsCancelled: (row.substitute_is_cancelled as boolean) || false,
        substituteCancelReason: (row.substitute_cancel_reason as string) || null,
        derivedCancelled: (row.derived_cancelled as boolean) || false,
        attendanceRecordedByName: (row.attendance_recorded_by_name as string) || null,
        attendanceRecordedAt: (row.attendance_recorded_at as string) || null,
        classAbsenceId: (row.class_absence_id as string) || null,
        classAbsenceType: (row.class_absence_type as string) || null,
        classAbsenceDestination: (row.class_absence_destination as string) || null,
        classAbsenceStartTime: (row.class_absence_start_time as string) || null,
        classAbsenceEndTime: (row.class_absence_end_time as string) || null,
        classAbsenceAttendanceRequired: (row.class_absence_attendance_required as boolean) ?? true,
        dailyPresenceStatus: (row.daily_presence_status as string) || null,
        dailyIsTripAttendance: (row.daily_is_trip_attendance as boolean) ?? false,
    }));
}

/**
 * Fetches attendance summary for a student on a specific date.
 */
export async function fetchAttendanceSummaryForDate(
    studentId: string,
    date: string
): Promise<Record<string, unknown>[]> {
    const { data, error } = await supabase.rpc('fn_get_attendance_summary_for_date' as never, {
        p_student_id: studentId,
        p_date: date,
    } as never);

    if (error) {
        throw new Error(`Failed to fetch attendance summary: ${error.message}`);
    }

    return Array.isArray(data) ? data : [];
}
